import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  loadGroupData, BarCompareConstNat, generateWardMapImage,
  fetchConstituencyBoundsFromGeo, WardGroupStaticMapWithBase, ConstituencyGroupStaticMap, latestElection,
  TopThreePrioritiesMini, getTopVI, generateComparisonBlurb,DemographicBlock
} from "./utils/metricsClient";
import { BestPMGrid } from "./utils/metricsClient";
import { MPCard, loadFullMpData, fetchLatestElection } from "./utils/metricsClient";

/* ─── Design tokens ─────────────────────────────────────────── */
const INK        = "#0f172a";
const INK2       = "#64748b";
const INK3       = "#94a3b8";
const BORDER     = "#e2e8f0";
const SURF       = "#f8fafc";
const STACK_TEAL = "#0abfbc";
const STACK_DARK = "#2c2c2a";
const BLUE       = "#185fa5";
const VIOLET     = "#5b21b6";
const AMBER      = "#92400e";
const POS        = "#15803d";
const NEG        = "#b91c1c";
const POSBG      = "#dcfce7";
const NEGBG      = "#fee2e2";
// Holiday Tax brand accent
const HT_ACCENT  = "#b45309";   // warm amber

const PARTY_COLORS = {
  "Conservative": "#0087DC", "Labour": "#D50000",
  "Liberal Democrat": "#FDBB30", "Reform UK": "#12B6CF", "Reform": "#12B6CF",
  "Green": "#008066", "SNP": "#FFF95D", "Plaid Cymru": "#3F8428", "Other": "#64748b",
};
const partyCol = (label) => PARTY_COLORS[label] || STACK_TEAL;
// Returns black for very light colours (e.g. SNP yellow) so text stays readable
const readableTextOn = (hex) => {
  const c = hex?.replace("#", "") || "000";
  const r = parseInt(c.substring(0,2),16), g = parseInt(c.substring(2,4),16), b = parseInt(c.substring(4,6),16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155 ? "#0f172a" : hex;
};

/* ─── Micro-components ───────────────────────────────────────── */
const SL = ({ children, color = STACK_TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "4px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: STACK_DARK, whiteSpace: "nowrap" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

const Callout = ({ eyebrow, headline, sub, implication, accentColor }) => (
  <Box sx={{
    border: `1px solid ${BORDER}`,
    borderTop: `3px solid ${accentColor}`,
    borderRadius: "10px",
    p: "10px 11px",
    bgcolor: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  }}>
    <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: accentColor, mb: "4px", lineHeight: 1.1 }}>
      {eyebrow}
    </Typography>
    <Typography sx={{ fontSize: 10.5, fontWeight: 750, lineHeight: 1.22, color: INK, mb: "5px", letterSpacing: "-0.01em" }}>
      {headline}
    </Typography>
    <Typography sx={{ fontSize: 8.2, color: INK2, lineHeight: 1.32 }}>
      {sub}
    </Typography>
    {implication && (
      <Box sx={{ mt: "7px", pt: "6px", borderTop: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 8, color: accentColor, lineHeight: 1.3, fontWeight: 650 }}>
          {implication}
        </Typography>
      </Box>
    )}
  </Box>
);

const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "4px",
      bgcolor: `${col}20`, border: `0.5px solid ${col}`,
      borderRadius: "10px", px: "8px", py: "4px",
      flexShrink: 0, minWidth: 120, justifyContent: "flex-start",
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {party}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

const InlineHeader = ({ constName, areaCode, topVI, latestElection: latestElectionProp }) => {
  const [latestElectionInternal, setLatestElectionInternal] = React.useState(null);
  const latestElection = latestElectionProp !== undefined ? latestElectionProp : latestElectionInternal;
  const [winner2019, setWinner2019]         = React.useState(null);

  React.useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    // Only fetch internally if parent hasn't lifted this state up
    if (latestElectionProp === undefined) {
      fetchLatestElection(areaCode).then(d => { if (!cancelled) setLatestElectionInternal(d); }).catch(() => {});
    }
    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then(r => r.text()).then(text => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      if (lines.length < 2) return;
      const header = lines[0].replace(/^\uFEFF/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), "Plaid Cymru": Number(row.pc19),
            SNP: Number(row.snp19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce(
            (a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other"
          );
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [areaCode]);

  const accent = latestElection?.party ? partyCol(latestElection.party)
    : topVI?.label ? partyCol(topVI.label) : HT_ACCENT;

  return (
    <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
        <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
          Holiday Tax Report
        </Typography>
        <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "8px", mb: "3px" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label="VI Projection" party={topVI?.label} share={topVI?.constituency} />
          <SlimPill label="2024 Result"   party={latestElection?.party} share={latestElection?.share} majority={latestElection?.majority} />
          <SlimPill label="2019 Result"   party={winner2019?.party} share={winner2019?.share} />
        </Box>
      </Box>
      <Box sx={{ height: 3, width: 180, borderRadius: 4, background: `linear-gradient(90deg, ${HT_ACCENT}, ${HT_ACCENT}55)` }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   HOLIDAY TAX REPORT VIEW
   Same layout as political report; demographics row is intentionally blank.
═══════════════════════════════════════════════════════════════ */

const PARTY_NAME_TO_CODE = {
  "Conservative": 1,
  "Labour": 2,
  "Reform UK": 3,
  "Reform": 3,
  "Liberal Democrat": 4,
  "Lib Dem": 4,
  "Green": 5,
  "SNP": 6,
  "Plaid Cymru": 7,
  "Plaid": 7,
};

const HolidayTaxReportView = ({ constName, mpName, party, roles, majority, mapImages, areaCode, height = "1123px", national = false }) => {
  const [charts, setCharts] = useState({ vi: [], national: [], priorities: [], best_pm: [], loading: true });
  const topVI = getTopVI(charts.vi);
  const prevArea = useRef(null);

  // Lift latestElection up so ROW 6 panels can use the actual 2024 MP party
  const [latestElection, setLatestElection] = useState(null);
  useEffect(() => {
    if (!areaCode) return;
    setLatestElection(null);
    fetchLatestElection(areaCode).then(setLatestElection).catch(() => {});
  }, [areaCode]);

  useEffect(() => {
    if (!areaCode || areaCode === prevArea.current) return;
    prevArea.current = areaCode;
    setCharts({ vi: [], national: [], priorities: [], loading: true });
    (async () => {
      try {
        const [vi, national, priorities, best_pm] = await Promise.all([
          loadGroupData({ groupId: "vote_intention",  areaCode, level: "constituency" }),
          loadGroupData({ groupId: "national_issues", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "priorities",      areaCode, level: "constituency" }),
          loadGroupData({ groupId: "best_pm",         areaCode, level: "constituency" }),
        ]);
        setCharts({ vi, national, priorities, best_pm, loading: false });
      } catch { setCharts(s => ({ ...s, loading: false })); }
    })();
  }, [areaCode]);

  const [mpData, setMpData] = useState(null);
  useEffect(() => {
    if (!constName) return;
    loadFullMpData(constName).then(setMpData);
  }, [constName]);

  /* ── Derived intelligence ───────────────────────────────────── */
  const viData = charts.vi || [];

  const topVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null, [viData]);
  // Use actual 2024 election winner, not VI projection
  const currentMpPartyFilter = useMemo(() => {
    const partyName = latestElection?.party;
    if (!partyName) return null;
    const code = PARTY_NAME_TO_CODE[partyName];
    if (!code) return null;
    return { code, label: `${partyName} (Current MP)` };
  }, [latestElection]);

  const secondVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[1] || null, [viData]);

  const viGap = useMemo(() =>
    topVIParty && secondVIParty
      ? +(Number(topVIParty.constituency) - Number(secondVIParty.constituency)).toFixed(1) : null,
  [topVIParty, secondVIParty]);

  const seatRisk = useMemo(() => {
    if (viGap == null) return null;
    if (viGap > 15) return { level: "Safe",          color: POS,        bg: POSBG };
    if (viGap > 7)  return { level: "Likely",         color: STACK_TEAL, bg: "#ccfbf1" };
    if (viGap > 3)  return { level: "Marginal",       color: AMBER,      bg: "#fef3c7" };
    return               { level: "Highly marginal", color: NEG,        bg: NEGBG };
  }, [viGap]);

  const topIssues = useMemo(() =>
    (charts.national || []).filter(d => Number.isFinite(Number(d?.constituency)))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency)).slice(0, 3),
  [charts.national]);

  const topIssueAboveNat = useMemo(() =>
    (charts.national || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null,
  [charts.national]);

  const topPriorityAboveNat = useMemo(() => {
    const top3 = (charts.priorities || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Math.abs(Number(d.constituency)) > 1e-6)
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))
      .slice(0, 3);
    return top3
      .filter(d => Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null;
  }, [charts.priorities]);

  // ── Holiday Tax panel data ───────────────────────────────────
  const [htBase, setHtBase] = useState({ vote: [], support: [], travel: [], loading: true });
  const [htViPartyVote, setHtViPartyVote] = useState([]);
  const [htTopPartySupport, setHtTopPartySupport] = useState([]);

  useEffect(() => {
    if (!areaCode) return;
    (async () => {
      try {
        const [vote, support, travel] = await Promise.all([
          loadGroupData({ groupId: "holiday_tax_vote",           areaCode }),
          loadGroupData({ groupId: "holiday_tax_support",        areaCode }),
          loadGroupData({ groupId: "tourist_tax_impact_travel",  areaCode }),
        ]);
        setHtBase({ vote, support, travel, loading: false });

      } catch (e) {
        console.error("HT panels error", e);
        setHtBase(s => ({ ...s, loading: false }));
      }
    })();
  }, [areaCode]);

  // Load current MP party-filtered support once latestElection resolves
  useEffect(() => {
    if (!areaCode || !currentMpPartyFilter?.code) return;
    loadGroupData({ groupId: "holiday_tax_support", areaCode, filters: { party: currentMpPartyFilter.code } })
      .then(setHtTopPartySupport)
      .catch(() => {});
  }, [areaCode, currentMpPartyFilter?.code]);

  useEffect(() => {
    if (!areaCode || !(charts.vi || []).length) return;
    const VI_KEY_TO_CODE = {
      "pol:vi:conservative:cat": 1,
      "pol:vi:labour:cat": 2,
      "pol:vi:reform:cat": 3,
      "pol:vi:libdem:cat": 4,
      "pol:vi:green:cat": 5,
      "pol:vi:snp:cat": 6,
    };
    const VI_CODE_LABEL = {
      1: "Con 24",
      2: "Lab 24",
      3: "RefUK 24",
      4: "LD 24",
      5: "Green 24",
      6: "SNP 24",
    };
    const VI_CODE_COLOR = {
      1: "#0087DC",
      2: "#D50000",
      3: "#12B6CF",
      4: "#FDBB30",
      5: "#008066",
      6: "#FFF95D",
    };
    const parties = [...(charts.vi || [])]
      .filter((d) => Number.isFinite(Number(d?.constituency)) && VI_KEY_TO_CODE[d?.key])
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))
      .slice(0, 5)
      .map((d) => ({ key: d.key, code: VI_KEY_TO_CODE[d.key] }));

    Promise.all(
      parties.map(async (p) => {
        const data = await loadGroupData({
          groupId: "holiday_tax_vote",
          areaCode,
          filters: { party: p.code },
        });
        return {
          code: p.code,
          label: VI_CODE_LABEL[p.code] || String(p.code),
          color: VI_CODE_COLOR[p.code] || "#64748b",
          data: data || [],
        };
      })
    )
      .then(setHtViPartyVote)
      .catch(() => setHtViPartyVote([]));
  }, [areaCode, charts.vi]);

  /* ── National variant ────────────────────────────────────────────
     Whole-GB tourism-tax profile: GB constituency maps, national values,
     no MP/seat-risk, national demographics, national tax-sentiment panels. */
  if (national) {
    const nat = (arr) => (arr || []).map((x) => ({ ...x, constituency: (x.national ?? x.constituency), national: null }));
    const viN = nat(charts.vi), issuesN = nat(charts.national), prioN = nat(charts.priorities);
    const voteN = nat(htBase.vote), supportN = nat(htBase.support), travelN = nat(htBase.travel);
    const topOf = (arr) => [...arr]
      .filter((d) => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null;
    const topVIn = topOf(viN), topIssueN = topOf(issuesN), topPrioN = topOf(prioN);
    const pct = (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : "—");

    return (
      <Box
        id="report-content"
        sx={{
          backgroundColor: "white", color: "black",
          width: "794px", height, overflow: "hidden",
          mx: "auto", p: "12px 18px",
          border: "1px solid #ccc", boxSizing: "border-box",
          display: "grid",
          gridTemplateRows: "auto 1.2fr auto auto 1.5fr",
          gridTemplateColumns: "1fr",
          gap: "7px",
        }}
      >
        {/* Header */}
        <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
            <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
              National Tourism-Tax Profile
            </Typography>
            <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.3 }}>
            {constName || "Great Britain"}
          </Typography>
          <Typography sx={{ fontSize: 10, color: INK2, mt: "1px" }}>
            {topVIn ? `${topVIn.label} leads nationally on ${pct(topVIn.constituency)} vote intention` : "Tourism-tax sentiment & political landscape"}
          </Typography>
        </Box>

        {/* Maps + VI chart */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", minHeight: 0 }}>
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <ConstituencyGroupStaticMap groupId="past_vote" />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Past Vote by Constituency</Typography>
            </Box>
          </Box>
          <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={viN} constName="Great Britain" hideNational />
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px", borderTop: `0.5px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>National Vote Intention</Typography>
            </Box>
          </Box>
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <ConstituencyGroupStaticMap groupId="vote_intention" />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Constituency</Typography>
            </Box>
          </Box>
        </Box>

        {/* Callouts */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
          <Callout eyebrow="Vote intention" accentColor={topVIn ? partyCol(topVIn.label) : HT_ACCENT}
            headline={topVIn ? `${topVIn.label} leads on ${pct(topVIn.constituency)}` : "Loading…"}
            sub={topVIn ? `${topVIn.label} is the strongest party nationally on projected vote intention.` : ""}
            implication="The national baseline for benchmarking tourism-tax constituencies." />
          <Callout eyebrow="Issue landscape" accentColor={BLUE}
            headline={topIssueN ? `${topIssueN.label} tops the national agenda` : "Loading…"}
            sub={topIssueN ? `${topIssueN.label} is the most cited important issue across GB (${pct(topIssueN.constituency)}).` : ""}
            implication={topIssueN ? `${topIssueN.label} is the dominant national frame.` : null} />
          <Callout eyebrow="Priority signal" accentColor={HT_ACCENT}
            headline={topPrioN ? `${topPrioN.label} is the leading priority` : "Loading…"}
            sub={topPrioN ? `${topPrioN.label} comes through as the top local priority nationally (${pct(topPrioN.constituency)}).` : ""}
            implication={topPrioN ? `A credible national lead theme for engagement.` : null} />
        </Box>

        {/* National demographics */}
        <Box sx={{ flexShrink: 0 }}>
          <DemographicBlock national constName="Great Britain" />
        </Box>

        {/* National tourism-tax panels */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", minHeight: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={HT_ACCENT}>Support for a tourist tax</SL>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={supportN} constName="Great Britain" hideNational />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={BLUE}>Impact on voting</SL>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={voteN} constName="Great Britain" hideNational />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={STACK_TEAL}>Impact on travel</SL>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={travelN} constName="Great Britain" hideNational />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      id="report-content"
      sx={{
        backgroundColor: "white",
        color: "black",
        width: "794px",
        height,
        overflow: "hidden",
        mx: "auto",
        p: "12px 18px",
        border: "1px solid #ccc",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateRows: "auto auto 1fr auto auto 1.5fr",
        gridTemplateColumns: "1fr",
        gap: "6px",
      }}
    >
      {/* ── ROW 1: HEADER ─────────────────────────────────────────── */}
      <InlineHeader constName={constName} areaCode={areaCode} topVI={topVI} latestElection={latestElection} />

      {/* ── ROW 2: MP CARD ────────────────────────────────────────── */}
      <Box sx={{ minHeight: 0, overflow: "hidden" }}>
        <MPCard mpData={mpData} />
      </Box>

      {/* ── ROW 3: MAPS + VI CHART ────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px", minHeight: 0 }}>

        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="past_vote" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Past Vote by Ward</Typography>
          </Box>
        </Box>

        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat
              data={charts.vi}
              constName={constName}
              firstRowBadge={seatRisk && (
                <span style={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: seatRisk.bg, border: `1px solid ${seatRisk.color}`,
                  borderRadius: 3, padding: "1px 6px", fontSize: 8, fontWeight: 700,
                  color: seatRisk.color, whiteSpace: "nowrap", flexShrink: 0, lineHeight: 1.5,
                }}>
                  {seatRisk.level} seat
                </span>
              )}
            />
          </Box>
          <Box sx={{ flexShrink: 0, textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px", borderTop: `0.5px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
              {`${constName || "Constituency"} Vote Intention vs National`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase groupId="vote_intention" areaCode={areaCode} />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Ward</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── ROW 4: CALLOUTS ───────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
        <Callout
          eyebrow="Seat dynamics"
          accentColor={seatRisk?.color || HT_ACCENT}
          headline={(() => {
            if (!seatRisk || !topVIParty) return "Loading…";
            return `${topVIParty.label} leads in a ${seatRisk.level.toLowerCase()} seat`;
          })()}
          sub={(() => {
            if (!topVIParty || !secondVIParty) return "";
            return `${Number(topVIParty.constituency).toFixed(1)}% for ${topVIParty.label} against ${Number(secondVIParty.constituency).toFixed(1)}% for ${secondVIParty.label}${majority ? `; 2024 majority ${Number(majority).toLocaleString()}` : ""}.`;
          })()}
          implication={(() => {
            if (!seatRisk) return null;
            if (seatRisk.level === "Safe") return `Public affairs engagement can lean into policy depth rather than electoral defensiveness.`;
            if (seatRisk.level === "Likely") return `This is competitive enough that strong local evidence should still land.`;
            if (seatRisk.level === "Marginal") return `Local salience matters here — constituency proof points will carry weight.`;
            return `Pressure is high in this seat, so campaigns need visible local relevance.`;
          })()}
        />
        <Callout
          eyebrow="Issue landscape"
          accentColor={BLUE}
          headline={(() => {
            if (!topIssueAboveNat) return topIssues[0] ? `${topIssues[0].label} leads locally` : "Loading…";
            return `${topIssueAboveNat.label} over-indexes most strongly`;
          })()}
          sub={(() => {
            if (!topIssueAboveNat) return "";
            const gap = topIssueAboveNat.gap;
            return `${Number(topIssueAboveNat.constituency).toFixed(1)}% here versus ${Number(topIssueAboveNat.national).toFixed(1)}% nationally (${gap > 0 ? "+" : ""}${gap}pp).`;
          })()}
          implication={(() => {
            if (!topIssueAboveNat) return null;
            return `${topIssueAboveNat.label} is the clearest route into the local conversation.`;
          })()}
        />
        <Callout
          eyebrow="Priority signal"
          accentColor={HT_ACCENT}
          headline={(() => {
            if (!topPriorityAboveNat) {
              const top = (charts.priorities || [])
                .filter(d => Number.isFinite(Number(d?.constituency)))
                .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0];
              return top ? `${top.label} comes through as the top priority` : "Loading…";
            }
            return `${topPriorityAboveNat.label} stands out in this constituency`;
          })()}
          sub={(() => {
            if (!topPriorityAboveNat) return "";
            return `${Number(topPriorityAboveNat.constituency).toFixed(1)}% locally versus ${Number(topPriorityAboveNat.national).toFixed(1)}% nationally (+${topPriorityAboveNat.gap}pp).`;
          })()}
          implication={(() => {
            if (!topPriorityAboveNat) return null;
            if (topPriorityAboveNat.gap > 8) return `This is a standout local priority and a strong lead frame for engagement.`;
            if (topPriorityAboveNat.gap > 3) return `This priority has more cut-through here than nationally.`;
            return `This priority is still credible locally, even without major divergence from the national picture.`;
          })()}
        />
      </Box>

      {/* ── ROW 5: DEMOGRAPHICS — intentionally blank ─────────────── */}
      <Box sx={{ flexShrink: 0 }}>
              <DemographicBlock constCode={areaCode} constName={constName} />
            </Box>

      {/* ── ROW 6: HOLIDAY TAX PANELS ────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: ".95fr 1.05fr", gap: "10px", minHeight: 0, height: "100%" }}>

        {/* LEFT: party deterrence bars + key callouts */}
        {(() => {
          const LESS_LIKELY_KEY = "holiday_tax_less_likely_pred";
          const getPct = (arr, field) => {
            const v = Number((arr || []).find((d) => d.key === LESS_LIKELY_KEY)?.[field]);
            return Number.isFinite(v) ? v : null;
          };
          const rows = (htViPartyVote || [])
            .map((p) => ({
              key: p.code,
              label: p.label,
              color: p.color,
              national: getPct(p.data, "national"),
              constituency: getPct(p.data, "constituency"),
            }))
            .filter(Boolean)
            .sort((a, b) => (Number(b.constituency) || -Infinity) - (Number(a.constituency) || -Infinity));
          const maxVal = Math.max(...rows.flatMap(r => [r.national ?? 0, r.constituency ?? 0]), 1);

          // Callout derived stats
          const incumbentRow = currentMpPartyFilter
            ? rows.find(r => r.key === currentMpPartyFilter.code) : null;
          const highestUplift = [...rows].sort((a, b) =>
            ((b.constituency ?? 0) - (b.national ?? 0)) - ((a.constituency ?? 0) - (a.national ?? 0))
          )[0] || null;
          const incumbentLabel = currentMpPartyFilter?.label?.replace(" (Current MP)", "") || "Incumbent";

          return (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
              <SL color={NEG}>Less likely to vote for party</SL>
              <Typography sx={{ fontSize: 7.5, color: INK2, mb: "4px", lineHeight: 1.3, flexShrink: 0 }}>
                If a holiday tax were introduced, share less likely to vote each party vs GB.
              </Typography>

              {/* Bar rows */}
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 0 }}>
                {rows.map((p, i) => {
                  const nat = p.national ?? 0;
                  const con = p.constituency ?? 0;
                  const natW = `${((nat / maxVal) * 100).toFixed(1)}%`;
                  const conW = `${((con / maxVal) * 100).toFixed(1)}%`;
                  return (
                    <Box key={p.key} sx={{
                      flex: 1, minHeight: 0, display: "flex", flexDirection: "column", width: "95%",
                      justifyContent: "center", pb: i < rows.length - 1 ? "2px" : 0,
                      borderBottom: i < rows.length - 1 ? `0.5px solid ${BORDER}` : "none",
                    }}>
                      <Typography sx={{ fontSize: 7.5, fontWeight: 600, color: INK2, mb: "2px" }}>{p.label}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "2px" }}>
                        <Box sx={{ height: 7, width: natW, bgcolor: `${p.color}55`, borderRadius: "2px", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 7, color: INK3 }}>{nat != null ? `${nat.toFixed(0)}%` : "—"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Box sx={{ height: 10, width: conW, bgcolor: p.color, borderRadius: "2px", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 7.5, fontWeight: 700, color: INK }}>{con != null ? `${con.toFixed(0)}%` : "—"}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Legend */}
              <Box sx={{ flexShrink: 0, display: "flex", gap: "10px", mt: "4px", mb: "6px" }}>
                {[{ label: "GB", opacity: 0.33 }, { label: constName || "Const.", opacity: 1 }].map(({ label, opacity }) => (
                  <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <Box sx={{ width: 10, height: 5, borderRadius: "1px", bgcolor: INK2, opacity }} />
                    <Typography sx={{ fontSize: 8, color: INK2 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>

              {/* ── Callout stats ── */}
              <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: "5px", width:"98%" }}>
                {incumbentRow && (
                  <Box sx={{
                    bgcolor: `${incumbentRow.color}10`,
                    border: `0.5px solid ${incumbentRow.color}50`,
                    borderLeft: `3px solid ${incumbentRow.color}`,
                    borderRadius: "6px", p: "5px 8px",
                  }}>
                    <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: readableTextOn(incumbentRow.color), mb: "2px" }}>
                      Incumbent — {incumbentLabel}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, lineHeight: 1 }}>
                        {incumbentRow.constituency != null ? `${incumbentRow.constituency.toFixed(0)}%` : "—"}
                      </Typography>
                      {incumbentRow.national != null && (
                        <Typography sx={{ fontSize: 8, color: INK2 }}>
                          vs {incumbentRow.national.toFixed(0)}% nationally
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 8, color: INK2, mt: "1px", lineHeight: 1.3 }}>
                      in {constName || "constituency"} say holiday tax makes them less likely to vote {incumbentLabel}
                    </Typography>
                  </Box>
                )}
                {(() => {
                  const gap = highestUplift?.constituency != null && highestUplift?.national != null
                    ? highestUplift.constituency - highestUplift.national : null;
                  const isSimilar = gap == null || Math.abs(gap) < 3;
                  return (
                    <Box sx={{
                      bgcolor: SURF,
                      border: `0.5px solid ${BORDER}`,
                      borderLeft: `3px solid ${isSimilar ? INK3 : readableTextOn(highestUplift.color)}`,
                      borderRadius: "6px", p: "5px 8px",
                    }}>
                      <Typography sx={{ fontSize: 7, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: isSimilar ? INK3 : readableTextOn(highestUplift.color), mb: "2px" }}>
                        {isSimilar ? "Local vs national" : "Biggest difference"}
                      </Typography>
                      {isSimilar ? (
                        <Typography sx={{ fontSize: 8, color: INK2, lineHeight: 1.35 }}>
                          This area is very similar to the national picture when it comes to the likelihood of the holiday tax impacting how people vote.
                        </Typography>
                      ) : (
                        <>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK, lineHeight: 1 }}>
                              {`+${gap.toFixed(0)}pp`}
                            </Typography>
                            <Typography sx={{ fontSize: 8, color: INK2 }}>above national</Typography>
                          </Box>
                          <Typography sx={{ fontSize: 8, color: INK2, mt: "1px", lineHeight: 1.3 }}>
                            {highestUplift.label} voters here are most deterred vs GB ({highestUplift.constituency?.toFixed(0)}% vs {highestUplift.national?.toFixed(0)}%)
                          </Typography>
                        </>
                      )}
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          );
        })()}

        {/* RIGHT: support breakdown + activity table */}
        <Box sx={{ display: "grid", gridTemplateRows: "1.25fr 0.75fr", gap: "8px", minHeight: 0, height: "100%" }}>

          {/* RIGHT-TOP: Holiday tax support grouped by context */}
          {(() => {
            const SUPPORT_KEYS = [
              { key: "holiday_tax_supp_strong_pred",    label: "Strongly support", short: "Str. sup",  color: "#1B6B2F" },
              { key: "holiday_tax_supp_some_pred",      label: "Somewhat support", short: "Smt. sup",  color: "#4CAF50" },
              { key: "holiday_tax_supp_neither_pred",   label: "Neither",          short: "Neither",   color: "#b0b8c4" },
              { key: "holiday_tax_supp_some_opp_pred",  label: "Somewhat oppose",  short: "Smt. opp",  color: "#EF5350" },
              { key: "holiday_tax_supp_strong_opp_pred",label: "Strongly oppose",  short: "Str. opp",  color: "#9B1C1C" },
            ];
            const getVal = (data, key, field) => {
              const v = Number(data?.find(d => d.key === key)?.[field]);
              return Number.isFinite(v) ? v : 0;
            };
            const incumbentShortLabel = currentMpPartyFilter?.label?.replace(" (Current MP)", "") || null;
            const contexts = [
              { label: "National",                    data: htBase.support, field: "national"     },
              { label: constName || "Constituency",   data: htBase.support, field: "constituency" },
              ...(incumbentShortLabel && htTopPartySupport.length ? [{
                label: `${incumbentShortLabel} voters`,
                data: htTopPartySupport,
                field: "constituency",
              }] : []),
            ];

            const PAD_L = 2, PAD_R = 2, PAD_TOP = 18, PAD_BOT = 22;
            const barW = 10, barGap = 1.5, groupGap = 16;
            const groupW = SUPPORT_KEYS.length * (barW + barGap) - barGap;
            const totalW = PAD_L + contexts.length * groupW + (contexts.length - 1) * groupGap + PAD_R;
            const chartH = 110;
            const plotH = chartH - PAD_TOP - PAD_BOT;
            const allVals = contexts.flatMap(c => SUPPORT_KEYS.map(sk => getVal(c.data, sk.key, c.field)));
            const maxVal = Math.max(...allVals, 1);
            const GRID_LINES = [25, 50, 75].filter(v => v < maxVal);

            return (
              <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
                <SL color={HT_ACCENT}>Support for tourist tax</SL>
                {/* Legend */}
                <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "3px 8px", mb: 0 }}>
                  {SUPPORT_KEYS.map(sk => (
                    <Box key={sk.key} sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: sk.color, flexShrink: 0 }} />
                      <Typography sx={{ alignContent: "center", fontSize: 8, color: INK2 }}>{sk.label}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                  <svg
                    width="100%" height="100%"
                    viewBox={`0 0 ${totalW} ${chartH}`}
                    preserveAspectRatio="xMidYMax meet"
                    style={{ display: "block" }}
                  >
                    {/* Background */}
                    <rect x="0" y="0" width={totalW} height={chartH} fill="#fafbfc" rx="3" />

                    {/* Gridlines */}
                    {GRID_LINES.map(g => {
                      const gy = PAD_TOP + plotH - (g / maxVal) * plotH;
                      return (
                        <g key={g}>
                          <line x1={PAD_L} y1={gy} x2={totalW - PAD_R} y2={gy} stroke="#e2e8f0" strokeWidth="0.5" strokeDasharray="2,2" />
                          <text x={PAD_L} y={gy - 1.5} fontSize="5" fill="#94a3b8">{g}%</text>
                        </g>
                      );
                    })}
                    {/* Baseline */}
                    <line x1={PAD_L} y1={PAD_TOP + plotH} x2={totalW - PAD_R} y2={PAD_TOP + plotH} stroke="#cbd5e1" strokeWidth="0.75" />

                    {/* Groups */}
                    {contexts.map((ctx, gi) => {
                      const gx = PAD_L + gi * (groupW + groupGap);
                      // Background band for group
                      const isConstituency = gi === 1;
                      return (
                        <g key={gi}>
                          {isConstituency && (
                            <rect x={gx - 3} y={PAD_TOP - 2} width={groupW + 6} height={plotH + 4} fill="#f0f7ff" rx="2" />
                          )}
                          {SUPPORT_KEYS.map((sk, bi) => {
                            const val = getVal(ctx.data, sk.key, ctx.field);
                            const barH = Math.max((val / maxVal) * plotH, 0.5);
                            const bx = gx + bi * (barW + barGap);
                            const by = PAD_TOP + plotH - barH;
                            return (
                              <g key={bi}>
                                <rect x={bx} y={by} width={barW} height={barH} fill={sk.color} rx="1.5" />
                                {val >= 6 && (
                                  <text x={bx + barW / 2} y={by - 2} textAnchor="middle" fontSize="5.5" fill={sk.color} fontWeight="700">
                                    {val.toFixed(0)}%
                                  </text>
                                )}
                              </g>
                            );
                          })}
                          {/* Group label — two lines if long */}
                          {(() => {
                            const words = ctx.label.split(" ");
                            const line1 = words.length > 2 ? words.slice(0, Math.ceil(words.length / 2)).join(" ") : ctx.label;
                            const line2 = words.length > 2 ? words.slice(Math.ceil(words.length / 2)).join(" ") : null;
                            const lx = gx + groupW / 2;
                            const ly = PAD_TOP + plotH + 6;
                            return (
                              <g>
                                <text x={lx} y={ly + (line2 ? 0 : 4)} textAnchor="middle" fontSize="6.5" fill={isConstituency ? BLUE : INK2} fontWeight={isConstituency ? "700" : "600"}>
                                  {line1}
                                </text>
                                {line2 && (
                                  <text x={lx} y={ly + 8} textAnchor="middle" fontSize="6.5" fill={isConstituency ? BLUE : INK2} fontWeight={isConstituency ? "700" : "600"}>
                                    {line2}
                                  </text>
                                )}
                              </g>
                            );
                          })()}
                        </g>
                      );
                    })}
                  </svg>
                </Box>
              </Box>
            );
          })()}

          {/* RIGHT-BOTTOM: Activity impact rows with icons */}
          {(() => {
            const NONE_KEY = "act_none_pred";
            const getActivityIcon = (label = "") => {
              const l = label.toLowerCase();
              if (l.includes("concert") || l.includes("festival") || l.includes("gig") || l.includes("music")) return "🎵";
              if (l.includes("museum") || l.includes("gallery") || l.includes("galleries"))                   return "🏛";
              if (l.includes("football") || l.includes("sport") || l.includes("live sport"))                  return "⚽";
              if (l.includes("theatre") || l.includes("show") || l.includes("comedy") || l.includes("cinema")) return "🎭";
              if (l.includes("restaurant") || l.includes("dining") || l.includes("eat") || l.includes("food")) return "🍽";
              if (l.includes("pub") || l.includes("bar") || l.includes("drink"))                              return "🍺";
              if (l.includes("hotel") || l.includes("stay") || l.includes("overnight"))                       return "🏨";
              if (l.includes("shop"))                                                                          return "🛍";
              if (l.includes("park") || l.includes("nature") || l.includes("outdoor") || l.includes("walk")) return "🌿";
              if (l.includes("beach") || l.includes("coast"))                                                 return "🏖";
              if (l.includes("visit") || l.includes("trip") || l.includes("travel"))                         return "✈";
              return "📍";
            };
            const ICON_BG = ["#dbeafe", "#dcfce7", "#fef3c7"];
            const ICON_COL = [BLUE, POS, HT_ACCENT];
            const topThreeActivities = (htBase.travel || [])
              .filter(d => d.key !== NONE_KEY && Number.isFinite(Number(d.constituency)))
              .sort((a, b) => Number(b.constituency) - Number(a.constituency))
              .slice(0, 3);
            return (
              <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
                <SL color={BLUE}>Top 3 activity impacts — {constName || "constituency"} vs GB</SL>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "5px", justifyContent: "space-around" }}>
                  {topThreeActivities.map((d, i) => {
                    const con = Number(d.constituency);
                    const nat = Number.isFinite(Number(d.national)) ? Number(d.national) : null;
                    const icon = getActivityIcon(d.label || d.key);
                    return (
                      <Box key={d.key} sx={{
                        display: "flex", alignItems: "center", gap: "8px",
                        bgcolor: "#fff", border: `0.5px solid ${BORDER}`,
                        borderRadius: "7px", p: "5px 8px",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      }}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: "6px",
                          bgcolor: ICON_BG[i], flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 14, lineHeight: 1,
                        }}>
                          {icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {d.label || d.key}
                          </Typography>
                          {nat != null && (
                            <Box sx={{ mt: "3px", height: 4, bgcolor: BORDER, borderRadius: "2px", overflow: "hidden", position: "relative" }}>
                              <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${nat}%`, bgcolor: ICON_COL[i], opacity: 0.35, borderRadius: "2px" }} />
                              <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${con}%`, bgcolor: ICON_COL[i], borderRadius: "2px" }} />
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 800, color: ICON_COL[i], lineHeight: 1 }}>
                            {con.toFixed(1)}%
                          </Typography>
                          {nat != null && (
                            <Typography sx={{ fontSize: 7, color: INK3, lineHeight: 1, mt: "1px" }}>
                              {nat.toFixed(1)}% GB
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}

        </Box>
      </Box>

    </Box>
  );
};

export default HolidayTaxReportView;
